import dayjs from "dayjs";
import { api } from "../../lib/api";
import { apiClient } from "../../lib/apiClient";
import { getFilterConfigForWidget } from "../widgetFilterConfig";
import { COMPONENT_API_MAP } from "../../lib/constants";
import { toISOEndOfDay } from "../../components/ai-insights/utils";
import type { ComponentKey } from "../types";
import type {
  ApiDashboard,
  ApiDashboardListItem,
  CustomDashboard,
  DashboardItem,
  WidgetCatalog,
  CreateDashboardPayload,
  UpdateDashboardPayload,
  RenderRequest,
  RenderResponse,
  WidgetFilterState,
  WidgetFilterConfig,
} from "../customDashboardTypes";

// ─── BE response wrappers ─────────────────────────────────────────────────────

interface ListResponse {
  status: string;
  dashboards: ApiDashboardListItem[];
}

interface DetailResponse {
  status: string;
  dashboard: ApiDashboard;
}

// ─── Conversion: API shape → local shape ─────────────────────────────────────

function apiDashboardToLocal(api: ApiDashboard): CustomDashboard {
  return {
    id: String(api.id),
    name: api.name,
    facilityId: api.facilityId,
    orgId: api.orgId,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
    sections: (api.sections ?? [])
      .sort((a, b) => a.position - b.position)
      .map((sec) => ({
        id: String(sec.id),
        name: sec.name,
        items: (sec.widgets ?? [])
          .sort((a, b) => a.position - b.position)
          .map((widget): DashboardItem => ({
            id: String(widget.id),
            widgetKey: widget.widgetKey,
          })),
      })),
  };
}

// ─── Conversion: list summary → local shape ───────────────────────────────────

function apiDashboardListItemToLocal(
  item: ApiDashboardListItem,
  facilityId: number,
): CustomDashboard {
  return {
    id: String(item.id),
    name: item.name,
    facilityId,
    orgId: 0,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt ?? item.createdAt,
    sections: (item.sections ?? []).map((sec, i) => ({
      id: String(i),
      name: sec.name,
      items: Array.from({ length: sec.widgetCount ?? 0 }, (_, j) => ({
        id: `list-${i}-${j}`,
        widgetKey: "",
      })),
    })),
  };
}

// ─── Conversion: local shape → replaceLayout payload ─────────────────────────

function buildReplacePayload(dashboard: CustomDashboard) {
  return {
    sections: dashboard.sections.map((sec, si) => ({
      name: sec.name,
      position: si,
      widgets: sec.items
        .filter((item) => item.widgetKey)
        .map((item, wi) => ({
          widget_key: item.widgetKey,
          position: wi,
          half_width: false,
        })),
    })),
  };
}

// ─── Widget render param builder ──────────────────────────────────────────────

// Certain widget keys expect date-only params (YYYY-MM-DD) instead of full
// datetimes (YYYY-MM-DDTHH:mm:ssZ). This is a BE API contract, not a UI choice.
const DATE_ONLY_WIDGET_KEYS = new Set([
  "energy_analysis.distribution",
  "energy_profile.section",
]);

function usesDateOnlyParams(widgetKey: string): boolean {
  if (DATE_ONLY_WIDGET_KEYS.has(widgetKey)) return true;
  if (widgetKey.startsWith("energy_analysis.profile")) return true;
  if (widgetKey === "energy_analysis.pattern") return true;
  return false;
}

// Inverse of toISOEndOfDay — recovers the originally-selected end date (for display,
// or for date-only API params) from the stored exclusive boundary (midnight UTC of
// the day after). Slicing to the date-only portion first avoids shifting the
// recovered day when the browser's local timezone isn't UTC.
export const fromISOEndOfDay = (iso: string): dayjs.Dayjs => dayjs(iso.slice(0, 10)).subtract(1, "day");

export function buildWidgetParams(
  widgetKey: string,
  filterState: WidgetFilterState,
  filterConfig: WidgetFilterConfig,
): Record<string, unknown> {
  const now = dayjs();
  const defaultStart = now.subtract(7, "day");

  if (filterConfig.type === "none") return {};

  if (filterConfig.type === "period") {
    return { period: filterState.period ?? filterConfig.defaultValue };
  }

  if (filterConfig.type === "date") {
    return { date: filterState.date ?? now.format("YYYY-MM-DD") };
  }

  if (filterConfig.type === "date_component") {
    // filterState.componentKey is stored in camelCase (e.g. "batteryDischarge").
    // COMPONENT_API_MAP converts it to the snake_case the API expects ("battery_discharge").
    const apiComponent = filterState.componentKey
      ? (COMPONENT_API_MAP[filterState.componentKey as ComponentKey] ?? filterState.componentKey)
      : null;
    return {
      date: filterState.date ?? now.format("YYYY-MM-DD"),
      components: apiComponent,
    };
  }

  if (filterConfig.type === "date_multicomponent") {
    // componentKeys is a camelCase array (e.g. ["load", "batteryEnergy"]).
    // Map each to snake_case and join as a comma-separated string for the API.
    const keys = filterState.componentKeys ?? ["load", "pv", "batteryEnergy", "gridImport"];
    const components = keys
      .map((k) => COMPONENT_API_MAP[k as ComponentKey] ?? k)
      .join(",");
    return {
      date: filterState.date ?? now.format("YYYY-MM-DD"),
      component_types: components,
    };
  }

  const useDateOnly = usesDateOnlyParams(widgetKey);
  // weekRange widgets store endDatetime as the plain selected date (date-only API,
  // no exclusive-boundary concept). Non-weekRange date-only widgets (e.g.
  // energy_analysis.distribution) share the same endDatetime storage as full-datetime
  // widgets — an exclusive boundary (midnight of the day after) — so it must be
  // shifted back one day to recover the actual selected end_date.
  const isWeekRangeStorage = "weekRange" in filterConfig && !!filterConfig.weekRange;

  // Slice the stored ISO string to YYYY-MM-DD rather than re-parsing with dayjs.
  // dayjs parses in local time, which flips near-midnight UTC values to the next/prev day.
  const startStr = useDateOnly
    ? (filterState.startDatetime?.slice(0, 10) ?? defaultStart.format("YYYY-MM-DD"))
    : (filterState.startDatetime ?? `${defaultStart.format("YYYY-MM-DD")}T00:00:00Z`);
  const endStr = useDateOnly
    ? (filterState.endDatetime
      ? (isWeekRangeStorage ? filterState.endDatetime.slice(0, 10) : fromISOEndOfDay(filterState.endDatetime).format("YYYY-MM-DD"))
      : now.format("YYYY-MM-DD"))
    : (filterState.endDatetime ?? toISOEndOfDay(now));

  const params: Record<string, unknown> = useDateOnly
    ? { start_date: startStr, end_date: endStr }
    : { start_datetime: startStr, end_datetime: endStr };

  if (
    filterConfig.type === "date_range_component" ||
    filterConfig.type === "date_range_component_resolution"
  ) {
    // Same camelCase → snake_case mapping as date_component above.
    const apiComponent = filterState.componentKey
      ? (COMPONENT_API_MAP[filterState.componentKey as ComponentKey] ?? filterState.componentKey)
      : null;
    params.component_type = apiComponent;
  }

  if (filterConfig.type === "date_range_component_resolution") {
    params.resolution = filterState.resolution ?? "hourly";
  }

  if (
    widgetKey.startsWith("energy_analysis.profile") ||
    (filterConfig.type === "date_range" && filterConfig.granularity)
  ) {
    params.granularity = filterState.granularity ?? "telemetry_interval";
  }

  if (filterState.scopeId && filterState.scopeId !== "overall") {
    params.scope_id = filterState.scopeId;
  }

  // PA and AI time-series charts need raw hourly data for their chart lines;
  // the BE only includes it when this flag is set to avoid bloating other responses.
  const PA_TIMESERIES_KEYS = [
    "performance_analysis",
    "operational_cost",
    "carbon_emissions",
    "resiliency_index",
    "dispatch_behavior",
    "aggregated_savings",
    "battery_dispatch",
    "load_solar_utility",
    "demand_peaks",
    "demand_charge_cost",
    "performance_metrics",
  ];
  if (PA_TIMESERIES_KEYS.some((key) => widgetKey.includes(key))) {
    params.include_timeseries = true;
  }

  return params;
}

// ─── Default filter state for initial render ──────────────────────────────────
// Used when a widget is first added or when the dashboard is rendered server-side
// without persisted per-widget filter state (e.g. full renderDashboard call).

function buildDefaultFilterState(
  widgetKey: string,
  defaultComponentKey?: string,
): WidgetFilterState {
  const { filterConfig, defaultOffsetDays } =
    getFilterConfigForWidget(widgetKey);
  if (filterConfig.type === "period") {
    return {
      period: (filterConfig as { type: "period"; defaultValue: string })
        .defaultValue,
    };
  }
  if (filterConfig.type === "date" || filterConfig.type === "date_component") {
    return { date: dayjs().format("YYYY-MM-DD") };
  }
  if (filterConfig.type === "date_multicomponent") {
    return { date: dayjs().format("YYYY-MM-DD"), componentKeys: ["load", "pv", "batteryEnergy", "gridImport"] };
  }
  if (filterConfig.type !== "none") {
    const now = dayjs();
    // weekRange widgets (Energy Pattern/Profile) send date-only params and store
    // the plain selected end date verbatim — no exclusive next-day-midnight
    // boundary, unlike the full-datetime widgets.
    const isWeekRangeWidget = "weekRange" in filterConfig && !!filterConfig.weekRange;
    const base: WidgetFilterState = {
      startDatetime: `${now.subtract(defaultOffsetDays, "day").format("YYYY-MM-DD")}T00:00:00Z`,
      endDatetime: isWeekRangeWidget ? now.format("YYYY-MM-DD") : toISOEndOfDay(now),
    };
    if (
      filterConfig.type === "date_range_component" ||
      filterConfig.type === "date_range_component_resolution"
    ) {
      if (defaultComponentKey) base.componentKey = defaultComponentKey;
    }
    if (filterConfig.type === "date_range_component_resolution") {
      base.resolution = "hourly";
    }
    return base;
  }
  return {};
}

// ─── Conversion: local shape → render payload ─────────────────────────────────

function buildRenderPayload(
  dashboard: CustomDashboard,
  defaultComponentKey?: string,
): RenderRequest {
  return {
    sections: dashboard.sections
      .filter((sec) => sec.items.some((it) => it.widgetKey))
      .map((sec) => ({
        section_id: Number(sec.id),
        widgets: sec.items
          .filter((item) => item.widgetKey)
          .map((item) => {
            const state =
              item.filterState ??
              buildDefaultFilterState(item.widgetKey, defaultComponentKey);
            const { filterConfig } = getFilterConfigForWidget(item.widgetKey);
            return {
              widget_key: item.widgetKey,
              params: buildWidgetParams(item.widgetKey, state, filterConfig),
            };
          }),
      })),
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const CustomDashboardService = {
  /**
   * Builds the JSON request body for the SSE stream endpoint.
   *
   * WHY THIS EXISTS AS A PUBLIC METHOD:
   * buildRenderPayload() is a private function that encapsulates filter-state
   * resolution and widget-params construction. useStreamDashboard needs the same
   * payload shape as the buffered /render path but cannot import a private function.
   * Exposing it here keeps the payload logic in one place rather than duplicating
   * it in the hook. The shape is identical to what /render already receives.
   */
  buildStreamPayload(dashboard: CustomDashboard, defaultComponentKey?: string): RenderRequest {
    return buildRenderPayload(dashboard, defaultComponentKey);
  },

  getWidgetCatalog() {
    return apiClient.get<WidgetCatalog>(
      "/api/v1/custom-dashboards/widget-catalog",
    );
  },

  listDashboards(facilityId: number): Promise<CustomDashboard[]> {
    return apiClient
      .get<ListResponse>(`/api/v1/facilities/${facilityId}/custom-dashboards`)
      .then((res) =>
        (res.dashboards ?? []).map((d) =>
          apiDashboardListItemToLocal(d, facilityId),
        ),
      );
  },

  getDashboard(facilityId: number, id: number): Promise<ApiDashboard> {
    return apiClient
      .get<DetailResponse>(
        `/api/v1/facilities/${facilityId}/custom-dashboards/${id}`,
      )
      .then((res) => res.dashboard);
  },

  createDashboard(
    facilityId: number,
    payload: CreateDashboardPayload,
  ): Promise<ApiDashboard> {
    return apiClient
      .post<
        DetailResponse,
        CreateDashboardPayload
      >(`/api/v1/facilities/${facilityId}/custom-dashboards`, payload)
      .then((res) => res.dashboard);
  },

  updateDashboard(
    facilityId: number,
    id: number,
    payload: UpdateDashboardPayload,
  ): Promise<ApiDashboard> {
    return apiClient
      .patch<
        DetailResponse,
        UpdateDashboardPayload
      >(`/api/v1/facilities/${facilityId}/custom-dashboards/${id}`, payload)
      .then((res) => res.dashboard);
  },

  replaceLayout(
    facilityId: number,
    id: number,
    dashboard: CustomDashboard,
  ): Promise<ApiDashboard> {
    return apiClient
      .put<
        DetailResponse,
        ReturnType<typeof buildReplacePayload>
      >(`/api/v1/facilities/${facilityId}/custom-dashboards/${id}/layout`, buildReplacePayload(dashboard))
      .then((res) => res.dashboard);
  },

  deleteDashboard(facilityId: number, id: number) {
    return apiClient.delete<void>(
      `/api/v1/facilities/${facilityId}/custom-dashboards/${id}`,
    );
  },

  renderDashboard(
    facilityId: number,
    id: number,
    dashboard: CustomDashboard,
    defaultComponentKey?: string,
    signal?: AbortSignal,
  ) {
    return apiClient.post<RenderResponse, RenderRequest>(
      `/api/v1/facilities/${facilityId}/custom-dashboards/${id}/render`,
      buildRenderPayload(dashboard, defaultComponentKey),
      { signal },
    );
  },

  exportDashboard(
    facilityId: number,
    dashboardId: number,
    payload: RenderRequest,
  ): Promise<{ blob: Blob; filename: string }> {
    return api
      .post(
        `/api/v1/facilities/${facilityId}/custom-dashboards/${dashboardId}/export`,
        payload,
        { responseType: "blob" },
      )
      .then((response) => {
        const disposition: string =
          response.headers["content-disposition"] ?? "";
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = disposition.match(/filename="?([^";\n]+)"?/);
        const timestamp = new Date()
          .toISOString()
          .replace(/[-:T]/g, "")
          .slice(0, 14);
        const filename = utf8Match
          ? decodeURIComponent(utf8Match[1])
          : (asciiMatch?.[1] ?? `dashboard_${dashboardId}_${timestamp}.xlsx`);
        return { blob: response.data as Blob, filename };
      });
  },

  renderWidget(
    facilityId: number,
    dashboardId: number,
    sectionId: number,
    widgetKey: string,
    filterState: WidgetFilterState,
    filterConfig: WidgetFilterConfig,
  ): Promise<RenderResponse> {
    const params = buildWidgetParams(widgetKey, filterState, filterConfig);
    const payload: RenderRequest = {
      sections: [
        { section_id: sectionId, widgets: [{ widget_key: widgetKey, params }] },
      ],
    };
    return apiClient.post<RenderResponse, RenderRequest>(
      `/api/v1/facilities/${facilityId}/custom-dashboards/${dashboardId}/render`,
      payload,
    );
  },

  // Exposed for use in hooks
  apiDashboardToLocal,
};
